/**
 * CaseStudyAskCard — Case-Study-Engine-Hook (modules-loop-b.md
 * "Case-Study-Engine-Hook (Marketing §1) — leichtes Setup in B1"): "nach 1.
 * Pilot: Testimonial-Ask (Zitat/Zahlen/'so lief die Übergabe')".
 *
 * Milestone (code-bestimmbar, gewählt aus den zwei spec-vorgeschlagenen
 * Optionen): die ERSTE akzeptierte Kanzlei-Verknüpfung — d. h. mindestens
 * einer der vom Owner selbst gesendeten Advisor-Invites hat
 * `status === 'accepted'` ({@link useAdvisorInviteFunnelStatus}.
 * `hasAcceptedInvite`). Bewusst NICHT der DATEV-Export-Erfolgsmoment
 * gewählt, damit dieser Hook nicht mit WL-F1 (Premium-Trigger 4) oder
 * WL-GTM-7 (b) um denselben Export-Moment konkurriert — eine akzeptierte
 * Verknüpfung ist zudem der fachlich stärkere Beleg für "die Übergabe hat
 * wirklich stattgefunden" als ein bloßer CSV-Download.
 *
 * Outer/Inner split (pattern: `KanzleiUebergabeMonthEndBanner`,
 * `AdvisorInviteDashboardCard`).
 *
 * Kein Dark-Pattern (Reviewer-Gate): einmalig, dismissbar (X ODER Klick auf
 * den Feedback-Link markieren beide dauerhaft als erledigt via
 * `caseStudyAskSuppression`, kein erneutes Nachfragen), keine
 * Fake-Dringlichkeit. Zielt auf `mailto:support@faebill.com` (bestehendes
 * Support-Postfach, s. `HomePage.tsx`/`ErrorBoundary.tsx`) — bewusst KEINE
 * neue Backend-Infra für diesen kleinen FE-Trigger (Modul-Scope-Vorgabe).
 *
 * @module components/tenant/CaseStudyAskCard
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose, IconCommentDots } from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { isLoopBBeta } from '../../config/loopBBetaAccess';
import { useAdvisorInviteFunnelStatus } from '../../hooks/useAdvisorInviteFunnelStatus';
import { dismissCaseStudyAsk, isCaseStudyAskDismissed } from '../../utils/caseStudyAskSuppression';

/**
 * Inner component — only mounted once the outer gate passes. Resolves the
 * milestone (`hasAcceptedInvite`) and renders nothing until it is reached.
 */
const CaseStudyAskCardInner: React.FC<{ uid: string }> = ({ uid }) => {
  const { t } = useTranslation('common');
  const [closed, setClosed] = useState(false);
  const { hasAcceptedInvite } = useAdvisorInviteFunnelStatus(true);

  if (closed || !hasAcceptedInvite) return null;

  const mailHref = `mailto:support@faebill.com?subject=${encodeURIComponent(
    t('caseStudyAsk.mailSubject'),
  )}&body=${encodeURIComponent(t('caseStudyAsk.mailBody'))}`;

  const handleDismiss = (): void => {
    dismissCaseStudyAsk(uid);
    setClosed(true);
  };

  const handleCtaClick = (): void => {
    // A click-through counts as "asked and answered" — never nag again.
    dismissCaseStudyAsk(uid);
    setClosed(true);
  };

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-3 rounded-lg border border-sky-600/50 bg-[var(--tone-info-bg)] p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--tone-info-bg)] text-[var(--tone-info-fg)]">
          <IconCommentDots size={16} />
        </span>
        <div>
          <p className="text-sm font-semibold text-[var(--tone-info-fg)]">{t('caseStudyAsk.title')}</p>
          <p className="mt-0.5 text-xs text-[var(--tone-info-fg)]">{t('caseStudyAsk.message')}</p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2 self-end sm:self-auto">
        <a
          href={mailHref}
          onClick={handleCtaClick}
          className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-sky-500"
        >
          {t('caseStudyAsk.cta')}
        </a>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('caseStudyAsk.dismiss')}
          className="text-[var(--tone-info-fg)] transition-colors hover:text-primary"
        >
          <IconClose size={14} />
        </button>
      </div>
    </div>
  );
};

/**
 * Outer visibility gate. Fail-closed: renders `null` unless EVERY cheap,
 * synchronous condition holds — Loop-B-beta, owner (not an advisor viewing a
 * mandant), viewing the user's OWN scope, and the ask was not already
 * dismissed/answered for this uid on this device.
 */
const CaseStudyAskCard: React.FC = () => {
  const { currentUser, sellerData, activeRole, effectiveScopeUid } = useAuth();

  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;
  const eligible =
    !!currentUser &&
    isLoopBBeta(currentUser.uid, sellerData) &&
    activeRole !== 'advisor' &&
    isOwnScope &&
    !isCaseStudyAskDismissed(currentUser.uid);

  if (!eligible || !currentUser) return null;
  return <CaseStudyAskCardInner uid={currentUser.uid} />;
};

export default CaseStudyAskCard;
